import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useNavigate, Link } from 'react-router-dom';
import {
  LayoutDashboard, Calendar, Users, UserCheck, List, CreditCard, Megaphone,
  Globe, BarChart, Settings, ChevronRight, Menu, Bell, Search, Plus,
  TrendingUp, TrendingDown, Activity, DollarSign, Clock, CheckCircle, X, LogOut, Shield, Package, FileText
} from 'lucide-react';
import CRMModule from './CRMModule';
import DaycareDashboard from './daycare/DaycareDashboard';
import DaycareModule from './daycare/DaycareModule';
import DaycareSettings from './daycare/DaycareSettings';
import DaycareLeadsKanban from './daycare/DaycareLeadsKanban';
import NotificationsBell from './daycare/NotificationsBell';
import ForcePasswordReset from './auth/ForcePasswordReset';
import CalendarPage from './CalendarPage';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Icon mapping for navigation
const NAV_ICONS = {
  'layout-dashboard': LayoutDashboard,
  'calendar': Calendar,
  'users': Users,
  'shield': Shield,
  'user-check': UserCheck,
  'list': List,
  'credit-card': CreditCard,
  'megaphone': Megaphone,
  'globe': Globe,
  'bar-chart': BarChart,
  'settings': Settings,
  'briefcase': List,
  'file-text': FileText,
  'check-circle': CheckCircle,
  'baby': Users,
  'home': LayoutDashboard,
  'zap': Activity,
  'monitor': LayoutDashboard,
  'folder': List,
  'server': List,
  'ticket': List,
  'activity': Activity,
  'package': Package,
  'sparkles': Activity,
  'dumbbell': Activity,
  'smile': Activity,
  'bell': Bell,
  'trending-up': TrendingUp,
};

export default function TenantDashboard() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [tenant, setTenant] = useState(null);
  const [template, setTemplate] = useState(null);
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [activePage, setActivePage] = useState('dashboard');
  const [branding, setBranding] = useState(null);
  
  // Dashboard stats
  const [stats, setStats] = useState({
    today_appointments: 12,
    revenue_today: 1850,
    new_clients: 5,
    pending_tasks: 8
  });

  useEffect(() => {
    // Guard: if this is a parent user, immediately redirect to /parent
    try {
      const stored = JSON.parse(localStorage.getItem('auth_user') || '{}');
      if (stored?.role === 'parent') {
        const slug = localStorage.getItem('tenant_slug') || '';
        navigate(slug ? `/${slug}/parent` : '/');
        return;
      }
    } catch { /* ignore */ }
    fetchTenantData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchTenantData = async () => {
    try {
      const tenantId = localStorage.getItem('tenant_id');
      
      const templatesRes = await axios.get(`${API}/templates`);
      const tenantsRes = await axios.get(`${API}/tenants`);
      
      // Find the logged-in tenant by ID, fall back to first tenant
      let t = null;
      if (tenantId) {
        t = tenantsRes.data.find(tn => tn.id === tenantId);
      }
      if (!t && tenantsRes.data.length > 0) {
        t = tenantsRes.data[0];
      }

      if (t) {
        setTenant(t);
        const tmpl = templatesRes.data.find(temp => temp.slug === t.template_slug);
        setTemplate(tmpl);
        // Fetch branding
        try {
          const brandRes = await axios.get(`${API}/branding/${t.id}`);
          setBranding(brandRes.data);
        } catch { /* use defaults */ }
      } else {
        const hairSalon = templatesRes.data.find(t => t.slug === 'hair_salon');
        if (hairSalon) {
          setTemplate(hairSalon);
          setTenant({
            name: 'Demo Salon',
            slug: 'demo',
            navigation: hairSalon.default_navigation,
            dashboard_config: hairSalon.default_dashboard,
            theme_config: hairSalon.default_theme
          });
        }
      }
    } catch (error) {
      console.error('Error fetching tenant data:', error);
      try {
        const templatesRes = await axios.get(`${API}/templates`);
        const hairSalon = templatesRes.data.find(t => t.slug === 'hair_salon');
        if (hairSalon) {
          setTemplate(hairSalon);
          setTenant({
            name: 'Demo Business',
            slug: 'demo',
            navigation: hairSalon.default_navigation,
            dashboard_config: hairSalon.default_dashboard,
            theme_config: hairSalon.default_theme
          });
        }
      } catch (e) {
        console.error('Error loading templates:', e);
      }
    } finally {
      setLoading(false);
    }
  };

  const isDaycare = tenant?.vertical === 'DAYCARE' || tenant?.template_slug === 'daycare';

  const navigation = tenant?.navigation || template?.default_navigation || [];
  const dashboardConfig = tenant?.dashboard_config || template?.default_dashboard || [];
  const themeConfig = tenant?.theme_config || template?.default_theme || {};

  // Compute effective branding colors
  const accentColor = branding?.primary_color || (isDaycare ? '#FACC15' : themeConfig.primary_color);
  const sidebarBg = branding?.secondary_color || '#1A2139';
  const brandName = branding?.brand_name || tenant?.name || 'DepinaCRM';
  const logoUrl = branding?.logo_url ? `${process.env.REACT_APP_BACKEND_URL}${branding.logo_url}` : '';

  const handleBrandingChange = (newBranding) => {
    setBranding(newBranding);
  };

  const renderStatWidget = (widget) => {
    const icons = {
      'calendar': Calendar,
      'dollar-sign': DollarSign,
      'user-plus': Users,
      'users': Users,
      'check-circle': CheckCircle,
      'clock': Clock,
      'briefcase': List,
      'activity': Activity,
      'ticket': List,
      'baby': Users,
      'home': LayoutDashboard
    };
    
    const IconComponent = icons[widget.icon] || Activity;
    const value = stats[widget.key] || Math.floor(Math.random() * 100);
    
    return (
      <div key={widget.key} className="stat-card" style={{ borderTopColor: themeConfig.primary_color }}>
        <div className="stat-icon" style={{ background: `${themeConfig.primary_color}15`, color: themeConfig.primary_color }}>
          <IconComponent size={24} />
        </div>
        <div className="stat-content">
          <span className="stat-label">{widget.title}</span>
          <span className="stat-value">
            {widget.title.toLowerCase().includes('revenue') ? `$${value.toLocaleString()}` : value}
          </span>
        </div>
        <div className="stat-trend up">
          <TrendingUp size={14} />
          <span>+12%</span>
        </div>
      </div>
    );
  };

  const renderListWidget = (widget) => {
    const demoItems = [
      { id: 1, title: 'Item 1', subtitle: 'Description', time: '9:00 AM' },
      { id: 2, title: 'Item 2', subtitle: 'Description', time: '10:30 AM' },
      { id: 3, title: 'Item 3', subtitle: 'Description', time: '2:00 PM' },
    ];
    
    return (
      <div key={widget.key} className="list-widget">
        <div className="widget-header">
          <h3>{widget.title}</h3>
          <button className="widget-action">View All</button>
        </div>
        <div className="widget-list">
          {demoItems.map(item => (
            <div key={item.id} className="list-item">
              <div className="item-dot" style={{ background: themeConfig.primary_color }} />
              <div className="item-content">
                <strong>{item.title}</strong>
                <span>{item.subtitle}</span>
              </div>
              <span className="item-time">{item.time}</span>
            </div>
          ))}
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="dashboard-loading">
        <div className="loading-spinner">Loading dashboard...</div>
      </div>
    );
  }

  // Force password reset check
  const authUser = JSON.parse(localStorage.getItem('auth_user') || '{}');
  if (authUser.must_change_password) {
    return <ForcePasswordReset onComplete={() => window.location.reload()} />;
  }

  return (
    <div className="tenant-dashboard" data-testid="tenant-dashboard">
      {/* Mobile overlay */}
      {mobileMenuOpen && (
        <div
          className="mobile-sidebar-overlay"
          onClick={() => setMobileMenuOpen(false)}
          data-testid="mobile-sidebar-overlay"
        />
      )}

      {/* Sidebar - Template Driven Navigation */}
      <aside className={`dashboard-sidebar ${sidebarOpen ? 'open' : 'collapsed'} ${mobileMenuOpen ? 'mobile-open' : ''}`} style={{ background: sidebarBg }}>
        <div className="sidebar-header">
          <div className="brand" style={{ color: accentColor }}>
            {logoUrl ? <img src={logoUrl} alt={brandName} className="brand-logo" data-testid="sidebar-logo" /> : null}
            <span>{brandName}</span>
          </div>
          <button className="toggle-btn desktop-only" onClick={() => setSidebarOpen(!sidebarOpen)}>
            <Menu size={20} />
          </button>
          <button className="toggle-btn mobile-only" onClick={() => setMobileMenuOpen(false)} data-testid="mobile-menu-close">
            <X size={20} />
          </button>
        </div>
        
        <nav className="sidebar-nav">
          {navigation.map((item) => {
            const IconComponent = NAV_ICONS[item.icon] || LayoutDashboard;
            const isActive = activePage === item.key;
            
            return (
              <Link
                key={item.key}
                to={item.path}
                className={`nav-link ${isActive ? 'active' : ''}`}
                onClick={(e) => {
                  setMobileMenuOpen(false);
                  if (item.path === '/admin/website-builder') {
                    // Allow navigation
                  } else {
                    e.preventDefault();
                    setActivePage(item.key);
                  }
                }}
                style={isActive ? { 
                  background: `${accentColor}15`,
                  borderLeftColor: accentColor,
                  color: accentColor
                } : {}}
                data-testid={`nav-${item.key}`}
              >
                <IconComponent size={20} />
                <span>{item.label}</span>
              </Link>
            );
          })}
        </nav>
        
        <div className="sidebar-footer">
          <button className="nav-link logout-link" onClick={() => {
            const slug = localStorage.getItem('tenant_slug') || tenant?.slug || '';
            localStorage.removeItem('auth_token');
            localStorage.removeItem('auth_user');
            localStorage.removeItem('tenant_id');
            localStorage.removeItem('tenant_slug');
            toast.success('Logged out');
            navigate(slug ? `/${slug}/login` : '/');
          }} data-testid="nav-logout">
            <LogOut size={20} />
            <span>Logout</span>
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <main className="dashboard-main">
        {/* Top Bar */}
        <header className="dashboard-topbar">
          <div className="topbar-left">
            <button
              className="mobile-hamburger mobile-only"
              onClick={() => setMobileMenuOpen(true)}
              data-testid="mobile-hamburger-btn"
            >
              <Menu size={22} />
            </button>
            <div>
              <h1>{activePage === 'dashboard' && isDaycare ? (brandName || tenant?.name || 'Daycare Admin') : navigation.find(n => n.key === activePage)?.label || 'Dashboard'}</h1>
              {activePage === 'dashboard' && isDaycare && <span className="topbar-subtitle">Management Overview</span>}
            </div>
          </div>
          <div className="topbar-right">
            <div className="search-box">
              <Search size={18} />
              <input type="text" placeholder="Search..." />
            </div>
            {isDaycare && tenant?.id ? (
              <NotificationsBell tenantId={tenant.id} accentColor={accentColor} onNavigate={setActivePage} />
            ) : (
              <button className="icon-btn" aria-label="Notifications">
                <Bell size={20} />
              </button>
            )}
            <div className="user-avatar" style={{ background: accentColor }}>
              {tenant?.name?.charAt(0) || 'U'}
            </div>
          </div>
        </header>

        {/* Dashboard Content - Template Driven Widgets */}
        <div className="dashboard-content">
          {isDaycare ? (
            /* ===== DAYCARE VERTICAL ===== */
            activePage === 'dashboard' ? (
              <DaycareDashboard tenantId={tenant?.id} onNavigate={setActivePage} brandName={brandName} />
            ) : activePage === 'settings' ? (
              <DaycareSettings tenantId={tenant?.id} onBrandingChange={handleBrandingChange} />
            ) : activePage === 'calendar' ? (
              <CalendarPage tenantId={tenant?.id} accentColor={accentColor} module="daycare" />
            ) : activePage === 'leads' ? (
              <DaycareLeadsKanban tenantId={tenant?.id} accentColor={accentColor} />
            ) : (
              <DaycareModule
                moduleKey={activePage}
                moduleName={navigation.find(n => n.key === activePage)?.label || activePage}
                tenantId={tenant?.id}
                themeColor={accentColor}
              />
            )
          ) : (
            /* ===== GENERIC CRM ===== */
            activePage === 'calendar' ? (
              <CalendarPage tenantId={tenant?.id} accentColor={accentColor} module="general" />
            ) : activePage === 'dashboard' ? (
            <>
              {/* Stats Row */}
              <div className="stats-grid">
                {dashboardConfig.filter(w => w.widget === 'stat').map(widget => renderStatWidget(widget))}
              </div>

              {/* Charts & Lists */}
              <div className="widgets-grid">
                {dashboardConfig.filter(w => w.widget === 'chart').map(widget => (
                  <div key={widget.key} className="chart-widget">
                    <div className="widget-header">
                      <h3>{widget.title}</h3>
                    </div>
                    <div className="chart-placeholder">
                      <BarChart size={48} style={{ color: themeConfig.primary_color }} />
                      <p>Chart visualization for {widget.key}</p>
                    </div>
                  </div>
                ))}
                
                {dashboardConfig.filter(w => w.widget === 'list').map(widget => renderListWidget(widget))}
              </div>

              {/* Quick Actions */}
              <div className="quick-actions">
                <h3>Quick Actions</h3>
                <div className="actions-grid">
                  <button 
                    className="action-card"
                    style={{ borderColor: themeConfig.primary_color }}
                    onClick={() => navigate('/admin/website-builder')}
                  >
                    <Globe size={24} style={{ color: themeConfig.primary_color }} />
                    <span>Edit Website</span>
                  </button>
                  <button 
                    className="action-card"
                    style={{ borderColor: themeConfig.accent_color }}
                  >
                    <Plus size={24} style={{ color: themeConfig.accent_color }} />
                    <span>New Appointment</span>
                  </button>
                  <button className="action-card">
                    <Users size={24} />
                    <span>Add Client</span>
                  </button>
                  <button className="action-card">
                    <CreditCard size={24} />
                    <span>Create Invoice</span>
                  </button>
                </div>
              </div>
            </>
          ) : (
            <CRMModule
              moduleKey={activePage}
              moduleName={navigation.find(n => n.key === activePage)?.label || activePage}
              tenantId={tenant?.id}
              themeColor={themeConfig.primary_color}
            />
            )
          )}
        </div>
      </main>
    </div>
  );
}
